import type { FC, FormEvent } from "react"
import React, { useState, memo } from "react"

import { AnimatePresence, LayoutGroup, motion } from "framer-motion"
import { useTranslation } from "react-i18next"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import styled, { css } from "styled-components"

import Button from "~/components/Button"
import { Wrapper } from "~/components/ui"
import {
  designRequestNotesState,
  designRequestState,
} from "~/containers/Proposal/state/design-request/use-design-request"
import type { UseDesign } from "~/containers/Proposal/state/designs/use-design"
import { currentPermissionsState } from "~/containers/Proposal/state/user/user-state"
import { canRejectDesignRequest } from "~/utils/canManipulateDesign"

import { AreaListItem } from "./AreaListItem"
import { HidePanelButton } from "./HidePanelButton"
import { ImageSelection } from "./ImageSelection"
import { PhaseList } from "./PhaseList"
import { Reject } from "./Reject"
import { SaveDesignButton } from "./SaveDesignButton"
import { Summary } from "./Summary"
import { ToolProperties } from "./ToolProperties"
import { Gap } from "../../proposal-styles"
import { useDesignerCapabilities } from "../designer-capabilities"
import { EscalationDrawerIsOpenState } from "../Drawer/Escalation"
import { areaIndex } from "../state/elements"
import { phaseState } from "../state/phase"
import { isLeftPanelOpenState } from "../state/view"
import { useLegacyRetiredImageryStatus } from "../useLegacyRetiredImageryStatus"

const SidePanel = styled(motion.div)`
  flex-shrink: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  ::-webkit-scrollbar {
    width: 0px;
  }
  background: #f7f7f8;
  ul,
  li {
    list-style: none;
  }

  ul {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  > * > li {
    min-height: 40px;
    border: transparent;
    border-bottom: 1px solid var(--grey-border);
    overflow-x: auto;
    overflow-y: hidden;
  }
`

const SidePanelInnerContainer = styled(motion.div)`
  flex-shrink: 0;
  position: relative;
  height: calc(100% - 50px);
  width: 275px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
`

const RejectModal = styled(motion.div)`
  position: absolute;
  height: 0px;
  bottom: 62px;
  left: 0px;
  border-radius: 7px;
  background-color: transparent;
  display: none;
  padding: var(--space-s);
  display: flex;
  gap: var(--space-xs);
  align-items: center;
  min-width: 275px;
`

const AnimatedSidePanelContainerInternal: FC<{
  children: React.ReactNode
  isPanelOpen: boolean
  setIsPanelOpen: React.Dispatch<React.SetStateAction<boolean>>
  isLeftSide: boolean
}> = ({ children, isPanelOpen, setIsPanelOpen, isLeftSide }) => {
  const xDirection = isLeftSide ? `-100%` : `100%`
  // had to add delay to the animation otherwise it will glitch and jump to the animate position.

  return (
    <SidePanel
      initial={{
        width: `0px`,
      }}
      animate={{
        width: isPanelOpen ? `auto` : `50px`,
        transition: {
          duration: 0.3,
        },
      }}
      exit={{
        width: `0px`,
      }}
      transition={{
        duration: 0.3,
      }}
    >
      <SidePanelInnerContainer
        initial={{
          x: xDirection,
        }}
        animate={{
          x: isPanelOpen ? 0 : xDirection,
          transition: {
            duration: 0.3,
          },
        }}
        exit={{
          x: xDirection,
        }}
        transition={{
          duration: 0.3,
        }}
      >
        {children}
      </SidePanelInnerContainer>
      <HidePanelButton
        setIsPanelOpen={setIsPanelOpen}
        isPanelOpen={isPanelOpen}
        isLeftSide={isLeftSide}
      />
    </SidePanel>
  )
}

export const AnimatedSidePanelContainer = React.memo(
  AnimatedSidePanelContainerInternal,
)

const EscalateButtonCSS = css`
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
  button {
    border: 1px solid #0066f8;
  }
  & > button:disabled {
    border: none;
    :hover {
      box-shadow: none;
    }
  }
`

const LeftPanel_Internal: FC<{ design: ReturnType<UseDesign> }> = ({
  design,
}) => {
  const { t } = useTranslation()
  const { continuePhase, designRequest: hasDesignRequest } =
    useDesignerCapabilities()
  const setEscalationDrawerIsOpen = useSetRecoilState(
    EscalationDrawerIsOpenState,
  )
  const [rejectModalActive, setRejectModalActive] = useState<boolean>(false)
  const designRequest = useRecoilValue(designRequestState)

  const currentPermissions = useRecoilValue(currentPermissionsState)
  const canReject = canRejectDesignRequest(currentPermissions)

  const close = () => setRejectModalActive(false)
  const { pathname } = window.location
  const isServiceQueueRoute =
    hasDesignRequest && pathname.includes(`service-queue`)
  const designRequestNotes = useRecoilValue(designRequestNotesState)

  const designHasBeenEscalated = designRequestNotes
    .map((note) => note.reason)
    .includes(`escalated`)

  const [isPanelOpen, setIsPanelOpen] = useRecoilState(isLeftPanelOpenState)

  return (
    <AnimatedSidePanelContainer
      setIsPanelOpen={setIsPanelOpen}
      isPanelOpen={isPanelOpen}
      isLeftSide={true}
      key={`left panel - internal`}
    >
      <LayoutGroup>
        <PhaseList continueOrComplete={continuePhase} />
      </LayoutGroup>
      <Gap />
      {canReject && isServiceQueueRoute && (
        <Wrapper css={EscalateButtonCSS} style={{ paddingBottom: `10px` }}>
          <Button onClick={() => setRejectModalActive(!rejectModalActive)}>
            reject
          </Button>
        </Wrapper>
      )}
      <AnimatePresence>
        {rejectModalActive && (
          <RejectModal
            initial={{ opacity: 0, y: `100%` }}
            animate={{ opacity: 1, y: `20%` }}
            exit={{ opacity: 0, y: `100%` }}
          >
            <Reject close={close} reqId={designRequest?.id} />
          </RejectModal>
        )}
      </AnimatePresence>
      {isServiceQueueRoute && (
        <Wrapper css={EscalateButtonCSS}>
          <Button
            onClick={() => setEscalationDrawerIsOpen(true)}
            disabled={!designRequest || designHasBeenEscalated}
          >
            {t(`escalate`)}
          </Button>
        </Wrapper>
      )}
      <SaveDesignButton design={design} />
    </AnimatedSidePanelContainer>
  )
}

export const LeftPanel = memo(LeftPanel_Internal)

export const RightPanel_Internal: FC<{
  save: () => void
}> = ({ save }) => {
  const areaIds = useRecoilValue(areaIndex)

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    save()
  }

  const [isPanelOpen, setIsPanelOpen] = React.useState(true)

  const phase = useRecoilValue(phaseState)
  // The unavailable-image notice carries its own picker; one picker keeps one confirm dialog
  const isImageUnavailable =
    useLegacyRetiredImageryStatus().status === `unavailable`

  return (
    <AnimatedSidePanelContainer
      setIsPanelOpen={setIsPanelOpen}
      isPanelOpen={isPanelOpen}
      isLeftSide={false}
    >
      <form onSubmit={onSubmit}>
        <LayoutGroup>
          {phase === `Panel Activation` && <Summary />}
          {phase === `Image Selection` && !isImageUnavailable && (
            <ImageSelection />
          )}
          <ToolProperties />
          <motion.ul layout>
            {areaIds.map((id, index) => (
              <AreaListItem key={`area-config-` + id} id={id} index={index} />
            ))}
          </motion.ul>
        </LayoutGroup>
      </form>
    </AnimatedSidePanelContainer>
  )
}

export const RightPanel = memo(RightPanel_Internal)
